import { useCallback, useContext, useEffect, useMemo } from 'react';

import { Flex, Text } from '@chakra-ui/react';
import { endOfDay, startOfDay } from 'date-fns';

import {
  CompletedDealsQuery,
  CompletedDealsQueryVariables,
  DealStateEnum,
  DealType,
  useCompletedDealsLazyQuery,
  useCompletedDealsQuery,
  useOnDealCompleteSubscription,
} from '../../gql/generated/graphql';

import ExportDealsToXlsxButton from '../ExportDealsToXlsxButton/ExportDealsToXlsxButton';
import LETable from '../shared/Table/LETable';

import { getUnseenMessageNotificationsCount } from '../../constants/notifications';
import usePaginationControls from '../../hooks/usePaginationControls';
import { useUser } from '../../hooks/useUser';
import { FiltersContext } from '../../libs/contextLib';
import {
  DealWithNotifications,
  completedDealColumns,
} from '../../pages/CompletedDealsPage/constants';
import { User } from '../../types/user';

type CompletedDealsTableProps = {
  startDate: Date;
  endDate: Date;
  addressState?: string;
  dealState?: DealStateEnum;
  notifications?: boolean;
  user?: User;
};

type CompletedDeal = NonNullable<
  NonNullable<CompletedDealsQuery['completedDeals']>['deals']
>[number];

const CompletedDealsTable = ({
  startDate,
  endDate,
  user,
  dealState,
  addressState,
  notifications,
}: CompletedDealsTableProps) => {
  const loggedUser = useUser();

  const {
    filters: {
      global: {
        selectedPodIds,
        sources,
        showAssignedToMe,
        showBootRequested,
        showAssignedToDeletedUsers,
        types,
        showActiveTags,
        showMatureFollowUps,
      },
    },
  } = useContext(FiltersContext);

  const filteredTypes = useMemo(
    () =>
      types.length
        ? types.filter((type) => type !== DealType.Acquisition)
        : Object.values(DealType).filter((type) => type !== DealType.Acquisition),
    [types],
  );
  const normalizedStartDate = startOfDay(startDate);
  const normalizedEndDate = endOfDay(endDate);

  const paginationControls = usePaginationControls();
  const { currentPage, itemsPerPage, setTotalRecords } = paginationControls;

  const baseQueryVariables: Omit<CompletedDealsQueryVariables, 'currentPage' | 'itemsPerPage'> =
    useMemo(
      () => ({
        assignedToMeFilter: showAssignedToMe,
        bootRequestedFilter: showBootRequested,
        assignedToDeletedUsersFilter: showAssignedToDeletedUsers,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        sources,
        types: filteredTypes,
        userId: user?.id,
        dealState,
        addressState,
        podIds: selectedPodIds,
        notifications,
        showActiveTags,
        showMatureFollowUps,
      }),
      [
        showAssignedToMe,
        showBootRequested,
        showAssignedToDeletedUsers,
        normalizedStartDate,
        normalizedEndDate,
        sources,
        filteredTypes,
        user?.id,
        dealState,
        addressState,
        selectedPodIds,
        notifications,
        showActiveTags,
        showMatureFollowUps,
      ],
    );

  const mapDealsWithNotifications = useCallback(
    (deals: (CompletedDeal | null | undefined)[]): DealWithNotifications[] =>
      deals
        .filter((deal): deal is NonNullable<CompletedDeal> => deal != null)
        .map((deal) => ({
          ...deal,
          unseenNotificationsCount: getUnseenMessageNotificationsCount(loggedUser.id, deal),
        })),
    [loggedUser.id],
  );

  const { data, loading, refetch } = useCompletedDealsQuery({
    skip: !sources.length,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      ...baseQueryVariables,
      currentPage,
      itemsPerPage,
    },
  });

  const [fetchAllCompletedDeals] = useCompletedDealsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const fetchAllDeals = useCallback(async (): Promise<DealWithNotifications[]> => {
    const result = await fetchAllCompletedDeals({ variables: baseQueryVariables });
    return mapDealsWithNotifications(result.data?.completedDeals?.deals ?? []);
  }, [fetchAllCompletedDeals, baseQueryVariables, mapDealsWithNotifications]);

  const dealsWithNotifications = useMemo<DealWithNotifications[]>(
    () => mapDealsWithNotifications(data?.completedDeals?.deals ?? []),
    [data?.completedDeals?.deals, mapDealsWithNotifications],
  );

  useEffect(() => {
    setTotalRecords(data?.completedDeals?.totalRecords ?? 0);
  }, [data?.completedDeals?.totalRecords, setTotalRecords]);

  useOnDealCompleteSubscription({
    variables: {
      sources,
      types: filteredTypes,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
    },
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData?.data?.onDealComplete?.length) {
        refetch();
      }
    },
  });

  return (
    <LETable
      columns={completedDealColumns}
      isLoading={loading}
      items={dealsWithNotifications}
      paginationControls={paginationControls}
      redirectToDeal
      rowKeyPrefix="completed-deal"
      rowKeyProp="id"
    >
      <Flex width="100%" justifyContent="space-between">
        <Text fontSize="20px" textColor="white">
          Completed Deals
        </Text>
        <ExportDealsToXlsxButton
          deals={dealsWithNotifications}
          columns={completedDealColumns}
          dealType="closed"
          onFetchAllDeals={fetchAllDeals}
        />
      </Flex>
    </LETable>
  );
};

export default CompletedDealsTable;
